import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Paper from "@mui/material/Paper";
import MUIDataTable from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import DATA_TABLE_THEME from "themes/DataTableTheme.js";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm } from "react-hook-form";
import EnumUtils from "utils/EnumUtils.js";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Tooltip from "@mui/material/Tooltip";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import OrganizationService from "services/OrganizationService.js";
import OrganizationAdminService from "services/OrganizationAdminService.js";
import PopUpModal from "components/utils/PopUpModal.js";
import InputErrorMessage from "components/utils/InputErrorMessage.js";
import COUNTRY_CODES_LIST from "utils/constants/CountryCodesList.js";
import RegexUtils from "utils/RegexUtils.js";
import useAPI from "hooks/useAPI";

function UserActionsCell({ user, onEdit, onLockChange, onBlockChange }) {
  const isUserLocked = Boolean(user.isLocked);
  const isUserBlocked = Boolean(user.isBlocked);

  const unlockUserAPI = useAPI(
    OrganizationAdminService.unlockUser,
    () => onLockChange(false),
    true
  );
  const lockUserAPI = useAPI(
    OrganizationAdminService.lockUser,
    () => onLockChange(true),
    true
  );
  const blockUserAPI = useAPI(
    OrganizationAdminService.blockUser,
    () => onBlockChange(true),
    true
  );
  const unblockUserAPI = useAPI(
    OrganizationAdminService.unblockUser,
    () => onBlockChange(false),
    true
  );

  function lockOrUnlockUser() {
    if (isUserLocked) {
      unlockUserAPI.execute(user.id);
    } else {
      lockUserAPI.execute(user.id);
    }
  }

  const actions = {
    startItems: [
      {
        component: <EditIcon fontSize="inherit" color="secondary" />,
        onClickHandler: onEdit,
      },
      {
        component: isUserBlocked ? (
          <LockIcon fontSize="inherit" color="custom.dark" />
        ) : isUserLocked ? (
          <Tooltip title="Tap to unlock user">
            <LockIcon fontSize="inherit" color="warning" />
          </Tooltip>
        ) : (
          <Tooltip title="Tap to lock user">
            <LockOpenIcon fontSize="inherit" color="warning" />
          </Tooltip>
        ),
        onClickHandler: () => {
          if (!isUserBlocked) lockOrUnlockUser();
        },
      },
      {
        component: isUserBlocked ? (
          <Tooltip title="Tap to unblock user">
            <DoDisturbOnIcon fontSize="inherit" color="error" />
          </Tooltip>
        ) : (
          <Tooltip title="Tap to block user">
            <BlockRoundedIcon fontSize="inherit" color="error" />
          </Tooltip>
        ),
        onClickHandler: () => {
          if (isUserBlocked) {
            unblockUserAPI.execute(user.id);
          } else {
            blockUserAPI.execute(user.id);
          }
        },
      },
    ],
    menuItems: [],
  };

  return <TableRowActionsCell actions={actions} />;
}

function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [roleTypeList, setRoleTypeList] = useState([]);
  const [hospitalList, setHospitalList] = useState([]);
  const [popUpModalData, setPopUpModalData] = useState({});
  const [tablePagination, setTablePagination] = useState({
    page: 0,
    rowsPerPage: 10,
  });
  const popUpModalRef = useRef(null);
  const usersRef = useRef(users);
  usersRef.current = users;

  const getUsersAPI = useAPI(
    OrganizationAdminService.getUsers,
    getUsersHandler,
    false,
    true
  );
  const getUserRoleTypesAPI = useAPI(
    OrganizationAdminService.getUserRoleTypes,
    getUserRoleTypesHandler,
    false,
    true
  );
  const getOrganizationHospitalsAPI = useAPI(
    OrganizationService.getOrganizationHospitals,
    getOrganizationHospitalsHandler,
    false,
    true
  );

  /* API success handlers */
  function getUsersHandler(response) {
    setUsers(response.data);
  }

  function getUserRoleTypesHandler(response) {
    let roleTypes = response.data;
    setRoleTypeList(roleTypes);
  }

  function getOrganizationHospitalsHandler(response) {
    let hospitals = response.data;
    setHospitalList(hospitals);
  }

  const updateUserStatus = useCallback((userId, updates) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId ? { ...user, ...updates } : user
      )
    );
  }, []);

  const tableData = useMemo(
    () =>
      users.map((user, index) => ({
        id: user.id,
        index: index + 1,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        countryCode: user.countryCode,
        phone: user.phone,
      })),
    [users]
  );

  const openModal = useCallback(() => {
    popUpModalRef.current.openModal();
  }, []);

  const dataTableColumns = useMemo(
    () => [
      {
        name: "id",
        label: "User ID",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "firstName",
        label: "First Name",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "lastName",
        label: "Last Name",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "email",
        label: "Email ID",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "countryCode",
        label: "Country Code",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "phone",
        label: "Phone",
        options: {
          filter: true,
          sort: false,
        },
      },
      {
        name: "actions",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_, tableMeta) => {
            const userId = tableMeta.rowData[0];
            const user = usersRef.current.find(
              (item) => item.id === Number(userId)
            );
            if (!user) return null;

            return (
              <UserActionsCell
                key={user.id}
                user={user}
                onEdit={() => {
                  setPopUpModalData(user);
                  openModal();
                }}
                onLockChange={(isLocked) =>
                  updateUserStatus(user.id, { isLocked })
                }
                onBlockChange={(isBlocked) =>
                  updateUserStatus(user.id, { isBlocked })
                }
              />
            );
          },
        },
      },
    ],
    [openModal, updateUserStatus]
  );

  const dataTableOptions = useMemo(
    () => ({
      filter: false,
      responsive: "standard",
      selectableRows: "none",
      download: false,
      print: false,
      jumpToPage: true,
      page: tablePagination.page,
      rowsPerPage: tablePagination.rowsPerPage,
      rowsPerPageOptions: [5, 10, 25],
      onChangePage: (currentPage) => {
        setTablePagination((prev) => ({ ...prev, page: currentPage }));
      },
      onChangeRowsPerPage: (rowsPerPage) => {
        setTablePagination({ page: 0, rowsPerPage });
      },
    }),
    [tablePagination.page, tablePagination.rowsPerPage]
  );

  return (
    <div className="page-shell">
      <div
        className="d-flex justify-content-end"
        style={{ marginTop: "-52px" }}
      >
        <Button
          size="medium"
          variant="contained"
          onClick={() => {
            setPopUpModalData({});
            openModal();
          }}
          startIcon={
            <img
              src="assets/plus-icon.svg"
              alt=""
              width={20}
              height={20}
            />
          }
        >
          Add User
        </Button>
      </div>
      <Paper elevation={0} className="surface-card section-card app-card">
        {getUsersAPI.status === "pending" ? (
          <div className="loading-state-shell text-center">
            <CircularProgress
              className="text-center"
              sx={{
                my: 2,
              }}
            />
          </div>
        ) : (
          <ThemeProvider theme={DATA_TABLE_THEME}>
            <MUIDataTable
              title={"Users"}
              data={tableData}
              columns={dataTableColumns}
              options={dataTableOptions}
            />
          </ThemeProvider>
        )}
        <PopUpModal ref={popUpModalRef}>
          <UserModal
            user={popUpModalData}
            roleTypeList={roleTypeList}
            hospitalList={hospitalList}
            closeModalHandler={closeModal}
          />
        </PopUpModal>
      </Paper>
    </div>
  );

  function closeModal() {
    popUpModalRef.current.closeModal();
    getUsersAPI.execute();
  }
}

function UserModal({ user, roleTypeList, hospitalList, closeModalHandler }) {
  const [isAddingUser, setIsAddingUser] = useState(
    Object.keys(user).length == 0
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  const addUserAPI = useAPI(
    OrganizationAdminService.addUser,
    () => closeModalHandler(),
    true
  );
  const updateUserAPI = useAPI(
    OrganizationAdminService.updateUser,
    () => closeModalHandler(),
    true
  );

  useEffect(() => {
    reset(user);
    setFormMultipleValueInputs();
  }, [user]);

  function setFormMultipleValueInputs() {
    if (!isAddingUser) {
      // Set roles
      setValue("roleType", user.roles[0].typeEnum);

      // Set associated hospitals
      for (let key in user.hospitals) {
        setValue(`associatedHospitalId-${user.hospitals[key].id}`, true);
      }
    }
  }

  function addOrUpdateUser(formData) {
    delete formData.hospitals;
    delete formData.roles;
    let userData = {};
    userData.roleTypes = [];
    userData.associatedHospitalIds = [];
    // Parse Role Types, Hospital Ids and assign other userData
    for (let input in formData) {
      if (input.includes("roleType") && formData[input]) {
        userData.roleTypes.push(formData[input]);
      } else if (input.includes("associatedHospitalId-") && formData[input]) {
        userData.associatedHospitalIds.push(parseInt(input.split("-")[1]));
      } else {
        userData[input] = formData[input];
      }
    }

    if (isAddingUser) {
      addUserAPI.execute(userData);
    } else {
      updateUserAPI.execute(formData.id, userData);
    }
  }

  return (
    <div>
      <form
        className="container py-4 px-4"
        onSubmit={handleSubmit(addOrUpdateUser)}
      >
        <h5 className="mb-4">
          {isAddingUser ? "Add " : "Update "}
          User
        </h5>
        <div className="row mb-4">
          <div className="col col-5 mb-3">
            <label htmlFor="firstNameInput" className="form-label">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              className="form-control"
              id="firstNameInput"
              {...register("firstName", {
                required: "First Name is required",
                pattern: {
                  value: RegexUtils.NAME,
                  message: "Fist Name is invalid",
                },
              })}
            />
            <InputErrorMessage name="firstName" errors={errors} />
          </div>
          <div className="col col-5 mb-3">
            <label htmlFor="lastNameInput" className="form-label">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              className="form-control"
              id="lastNameInput"
              {...register("lastName", {
                required: "Last Name is required",
                pattern: {
                  value: RegexUtils.NAME,
                  message: "Last Name is invalid",
                },
              })}
            />
            <InputErrorMessage name="lastName" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="emailInput" className="form-label">
              Email ID
            </label>
            <input
              type="email"
              name="email"
              className="form-control"
              id="emailInput"
              {...register("email", {
                required: "Email ID is required",
                pattern: {
                  value: RegexUtils.EMAIL,
                  message: "Email ID is invalid",
                },
              })}
            />
            <InputErrorMessage name="email" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="phoneInput" className="form-label">
              Phone
            </label>
            <input
              type="text"
              name="phone"
              className="form-control"
              id="phoneInput"
              {...register("phone", {
                required: "Phone is required",
                pattern: {
                  value: RegexUtils.PHONE,
                  message: "Phone is invalid",
                },
              })}
            />
            <InputErrorMessage name="phone" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="countryCode" className="form-label">
              Country Code
            </label>
            <select
              className="form-select"
              id="countryCodeInput"
              aria-label="Default select example"
              {...register("countryCode", {
                required: "Country Code is required",
                minLength: {
                  value: 2,
                  message: "Country Code must exceed 2 characters",
                },
                maxLength: {
                  value: 5,
                  message: "Country Code must not exceed 5 characters",
                },
              })}
            >
              <option></option>
              {COUNTRY_CODES_LIST.map((item, index) => (
                <option key={item.value + index} value={item.value}>
                  {item.country}
                </option>
              ))}
            </select>
            <InputErrorMessage name="countryCode" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label className="form-label d-block">Role Types</label>
            {roleTypeList.map((role, index) => {
              return (
                <div
                  key={`roleIndex-${index}`}
                  className="form-check form-check-inline"
                >
                  <input
                    className="form-check-input"
                    type="radio"
                    name="roleType"
                    id={`roleTypeInput-${role.typeEnum}`}
                    value={role.typeEnum}
                    {...register("roleType")}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`roleTypeInput-${role.typeEnum}`}
                  >
                    {EnumUtils.parse(role.typeEnum)}
                  </label>
                </div>
              );
            })}
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="associatedHospitalIdInput" className="form-label">
              Associated Centres
            </label>
            {hospitalList.map((hospital, index) => {
              return (
                <div
                  key={`hospitalIndex-${index}`}
                  className="form-check form-check-inline"
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name={`associatedHospitalId-${hospital.id}`}
                    id={`associatedHospitalIdInput-${hospital.id}`}
                    {...register(`associatedHospitalId-${hospital.id}`)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`associatedHospitalIdInput-${hospital.id}`}
                  >
                    {hospital.hospitalName}
                  </label>
                </div>
              );
            })}
            <InputErrorMessage name="associatedHospitalId" errors={errors} />
          </div>
        </div>
        {(
          isAddingUser
            ? addUserAPI.status === "pending"
            : updateUserAPI.status === "pending"
        ) ? (
          <LoadingButton
            loading
            loadingPosition="start"
            startIcon={<SaveIcon />}
            variant="contained"
            className="me-3"
          >
            {isAddingUser ? "Add" : "Update"}
          </LoadingButton>
        ) : (
          <Button
            type="submit"
            className="me-3"
            startIcon={<DoneIcon />}
            variant="contained"
          >
            {isAddingUser ? "Add" : "Update"}
          </Button>
        )}
        <Button onClick={() => closeModalHandler()} variant="outlined">
          Cancel
        </Button>
      </form>
    </div>
  );
}

export default UsersManagement;
